import { useEffect, useState } from "react";

import Button from "../../ui/buttons/Button";
import CompanyPicker from "./CompanyPicker";
import { contactsService } from "../../services/contacts.service";
import { genericService } from "../../services/generic.service";
import { useCompanyOptions } from "../../hooks/useCompanyOptions";
import { describeRequestError } from "../../utils/requestErrorMessage";

import styles from "./QuickContactForm.module.css";

const WORK_EMAIL_TYPE_NAME = "work e-mail";

export const splitName = (fullName, fallbackEmail) => {
  const parts = String(fullName ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    const localPart = String(fallbackEmail ?? "").split("@")[0];
    return { firstName: localPart, lastName: "" };
  }

  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
};

const pickMobileType = (contactTypes) =>
  contactTypes.find((type) => /mobile|celular|m[oó]vil/i.test(type?.name || "")) ||
  contactTypes[0] ||
  null;

const pickWorkEmailType = (emailTypes) =>
  (emailTypes ?? []).find(
    (type) => type?.name?.trim().toLowerCase() === WORK_EMAIL_TYPE_NAME,
  ) ?? null;

/**
 * Alta rápida de un contacto a partir de una dirección de correo.
 *
 * Crea la persona en la capa de contactos de la cuenta indicada con el email
 * como laboral y, opcionalmente, posición, empresa y celular. La empresa se
 * propone a partir del lead, si viene uno.
 */
function QuickContactForm({ entry, account, lead, onCancel, onCreated }) {
  const hasLead = Boolean(lead);
  const initialName = splitName(entry.name, entry.email);
  const [firstName, setFirstName] = useState(initialName.firstName);
  const [lastName, setLastName] = useState(initialName.lastName);
  const [position, setPosition] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState(() =>
    lead?.company?.id ? { id: lead.company.id, name: lead.company.name ?? "" } : null,
  );
  const {
    options: companyItems,
    loading: companiesLoading,
    search: searchCompanies,
    loadMore: loadMoreCompanies,
    pin: pinCompany,
  } = useCompanyOptions({ accountId: account?.id });
  const [fieldTypes, setFieldTypes] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    genericService
      .personFieldTypes()
      .then((data) => {
        if (cancelled) return;
        setFieldTypes({
          emailTypes: data?.email_types ?? [],
          contactTypes: data?.contact_types ?? [],
        });
      })
      .catch(() => {
        if (!cancelled) setFieldTypes({ emailTypes: [], contactTypes: [] });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    searchCompanies("");
  }, [searchCompanies]);

  useEffect(() => {
    const leadCompany = lead?.company;
    setCompany(leadCompany?.id ? { id: leadCompany.id, name: leadCompany.name ?? "" } : null);
  }, [lead]);

  const handleSave = async () => {
    const emailType = pickWorkEmailType(fieldTypes?.emailTypes);
    if (!emailType) {
      setError(
        fieldTypes
          ? "No se encontró el tipo de email laboral (Work E-mail) en la base de contactos. Cerrá el formulario y volvé a abrirlo."
          : "Todavía se están cargando los tipos de email, probá de nuevo en un segundo.",
      );
      return;
    }
    if (!firstName.trim()) {
      setError("El nombre es obligatorio.");
      return;
    }

    const mobileType = pickMobileType(fieldTypes.contactTypes);
    const payload = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      position: position.trim(),
      account_id: account?.id,
      company: company?.id ?? null,
      emails: [{ type: emailType.id, data: entry.email, active: true }],
      contacts:
        phone.trim() && mobileType
          ? [{ type: mobileType.id, data: phone.trim() }]
          : [],
    };

    setSaving(true);
    setError(null);
    try {
      const created = await contactsService.create(payload);
      onCreated?.({
        id: created?.id,
        name: `${created?.first_name ?? firstName.trim()} ${created?.last_name ?? lastName.trim()}`.trim(),
        position: created?.position ?? position.trim(),
        company_id: created?.company ?? company?.id ?? null,
        company_name: created?.company_name ?? company?.name ?? "",
        email: entry.email,
      });
    } catch (requestError) {
      setError(describeRequestError(requestError, "No se pudo crear el contacto"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.quickForm} data-testid="quick-contact-form">
      <div className={styles.quickGrid}>
        <input
          className={styles.quickInput}
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
          placeholder="Nombre"
          aria-label="Nombre"
        />
        <input
          className={styles.quickInput}
          value={lastName}
          onChange={(event) => setLastName(event.target.value)}
          placeholder="Apellido"
          aria-label="Apellido"
        />
        <input
          className={`${styles.quickInput} ${styles.quickFull}`}
          value={position}
          onChange={(event) => setPosition(event.target.value)}
          placeholder="Posición"
          aria-label="Posición"
        />
        <div className={styles.quickFull}>
          <CompanyPicker
            className={styles.quickDropdown}
            items={companyItems}
            value={company}
            placeholder="Empresa (opcional)"
            loading={companiesLoading}
            accountId={account?.id ?? null}
            onSearch={searchCompanies}
            onLoadMore={loadMoreCompanies}
            onChange={setCompany}
            onCreated={pinCompany}
          />
        </div>
        <input
          className={`${styles.quickInput} ${styles.quickFull} ${styles.quickReadonly}`}
          value={entry.email}
          readOnly
          aria-label="Email"
        />
        <input
          className={`${styles.quickInput} ${styles.quickFull}`}
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          placeholder="Celular (opcional)"
          aria-label="Celular"
        />
      </div>
      <p className={styles.quickNote}>
        Se crea en la capa de contactos de <b>{account?.name}</b>.
      </p>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <div className={styles.quickActions}>
        <button
          type="button"
          className={styles.miniButton}
          onClick={onCancel}
          disabled={saving}
        >
          Cancelar
        </button>
        <Button
          variant="primary"
          tone="blue"
          height={32}
          onClick={handleSave}
          loading={saving}
          disabled={saving}
        >
          {hasLead ? "Guardar y sumar al lead" : "Guardar contacto"}
        </Button>
      </div>
    </div>
  );
}

export default QuickContactForm;
